import React, { useEffect, useState } from 'react'
import { Avatar, Box, Button, Chip, CircularProgress, Divider, IconButton, InputAdornment, List, ListItem, ListItemAvatar, ListItemText, Stack, TextField, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDispatch, useSelector } from 'react-redux';
import { selectWhatsappState } from '../../state/whatsapp/whatsappSelector';
import Styles from './whatsapp.module.scss'
import WhatsappTemplateModal from './WhatsappTemplateModal';
import { getUserList, getUserMessagesFrequent, sendGenericTemplateMessage } from '../../state/whatsapp/whatsappSlice';
import { formatWhatsappPhone, getPreviewBody, getVariablesFromBody } from '../../helpers/utils/textFormatter';

const WhatsappSidebar = ({ selectedContact, onSelectContact }) => {
    const dispatch = useDispatch();
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'replied', 'not_replied'
    const [page, setPage] = useState(1);
    const perPage = 10;

    const { userList, userListPagination, isUserListLoading, isSending, templates } = useSelector(selectWhatsappState);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

    useEffect(() => {
        if (userList && userList.length > 0 && !selectedContact) {
            onSelectContact(userList[0]);
        }
    }, [userList, selectedContact, onSelectContact]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        setPage(1);
        dispatch(getUserList({ page: 1, perPage, searchPhone: value, status: filterStatus }));
    };

    const handleStatusChange = (newStatus) => {
        setFilterStatus(newStatus);
        setPage(1);
        dispatch(getUserList({ page: 1, perPage, searchPhone: searchQuery, status: newStatus }));
    };

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 50) {
            if (!isUserListLoading && userListPagination && userListPagination.hasNextPage) {
                const nextPage = (userListPagination.page || page) + 1;
                setPage(nextPage);
                dispatch(getUserList({ page: nextPage, perPage, searchPhone: searchQuery, status: filterStatus }));
            }
        }
    };

    const handleRefresh = () => {
        setPage(1);
        const phone = selectedContact.phone.replace('whatsapp:', '').replace('+', '');
        dispatch(getUserMessagesFrequent({ phone, page: 1, perPage: 1000 }));
        dispatch(getUserList({ page, perPage, searchPhone: searchQuery, status: filterStatus }));
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        if (isToday) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    };

    const handleSendTemplateMessage = (templateVariables, selectedTemplateId, phoneNumber) => {
        const activeTemplate = templates.find(t => t.sid === selectedTemplateId);
        if (!activeTemplate) return;

        const toPhone = phoneNumber.replace('whatsapp:', '');
        const formattedVars = {};
        const varKeys = activeTemplate.variables && Object.keys(activeTemplate.variables).length > 0
            ? Object.keys(activeTemplate.variables).sort((a, b) => Number(a) - Number(b))
            : getVariablesFromBody(activeTemplate.body);

        varKeys.forEach(k => {
            formattedVars[k] = templateVariables[k] || "";
        });

        const bodyPreview = getPreviewBody(activeTemplate.body, templateVariables);
        dispatch(sendGenericTemplateMessage({
            to: toPhone,
            templateSid: activeTemplate.sid,
            variables: formattedVars,
            body: bodyPreview
        }));

        setIsTemplateModalOpen(false);
    }
    return (
        <Box className={Styles.sidebar} onScroll={handleScroll}>
            <Box className={Styles.sidebarHeader} sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search contacts..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setIsTemplateModalOpen(true)}
                    >
                        <AddIcon />
                    </Button>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1} mt={1} justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Chip
                            label="All"
                            variant={filterStatus === 'all' || !filterStatus ? 'filled' : 'outlined'}
                            color='primary'
                            onClick={() => handleStatusChange('all')}
                        />
                        <Chip
                            label="Replied"
                            variant={filterStatus === 'replied' ? 'filled' : 'outlined'}
                            color='success'
                            onClick={() => handleStatusChange(filterStatus === 'replied' ? 'all' : 'replied')}
                        />
                        <Chip
                            label="Not Replied"
                            variant={filterStatus === 'not_replied' ? 'filled' : 'outlined'}
                            color='warning'
                            onClick={() => handleStatusChange(filterStatus === 'not_replied' ? 'all' : 'not_replied')}
                        />
                    </Stack>
                    <Button onClick={handleRefresh} variant="outlined" color="primary" size='small'>
                        <RefreshIcon />
                    </Button>
                </Stack>
                <Divider sx={{ mt: 2 }} />
            </Box>
            {isUserListLoading && (userList?.length === 0 || page === 1) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <>
                    <List disablePadding>
                        {(userList || []).map((contact) => (
                            <React.Fragment key={contact.messageSid}>
                                <ListItem
                                    className={`${Styles.sidebarItem} ${selectedContact?.phone === contact.phone ? Styles.sidebarItemActive : ''}`}
                                    onClick={() => onSelectContact(contact)}
                                    alignItems="flex-start"
                                    sx={{ px: 2, py: 1.5 }}
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: '#25D366', color: '#fff' }} />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="subtitle2" noWrap>
                                                    {formatWhatsappPhone(contact.phone)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatTime(contact.messageAt)}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '70%' }}>
                                                    {contact.lastMessage}
                                                </Typography>
                                                {contact.status && (
                                                    <Chip
                                                        label={contact.status}
                                                        size="small"
                                                        sx={{
                                                            fontSize: 10,
                                                            height: 20,
                                                            bgcolor: contact.status === 'delivered' ? '#e8f5e9'
                                                                : contact.status === 'read' ? '#e3f2fd'
                                                                    : (contact.status === 'undelivered' || contact.status === 'failed') ? '#fce4ec'
                                                                        : '#f0ea9cff',
                                                            color: contact.status === 'delivered' ? '#2e7d32'
                                                                : contact.status === 'read' ? '#1565c0'
                                                                    : (contact.status === 'undelivered' || contact.status === 'failed') ? '#c62828'
                                                                        : '#616161ff',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        }
                                    />
                                </ListItem>
                                <Divider component="li" />
                            </React.Fragment>
                        ))}
                        {(!userList || userList.length === 0) && !isUserListLoading && (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    No contacts found
                                </Typography>
                            </Box>
                        )}
                    </List>
                    {isUserListLoading && page > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                </>
            )}

            <WhatsappTemplateModal
                title="Create Conversation"
                isLoading={isSending}
                open={isTemplateModalOpen}
                onClose={() => setIsTemplateModalOpen(false)}
                onSubmit={handleSendTemplateMessage}
            />
        </Box>
    )
}

export default WhatsappSidebar
